import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Typography,
  Layout,
  DatePicker,
  Space,
  Badge,
  Tooltip,
  Button,
} from "antd";
import dayjs from "dayjs";
import { postAPI } from "../../../utils/common";
import {
  BOOKING_BY_ID,
  IMAGE_BASE_URL,
  ROOM_VIEW,
} from "../../../constants/api";
import LayoutHeader from "../../inc/LayoutHeader";
import LayoutFooter from "../../inc/LayoutFooter";
import {
  bookingStatusColors,
  bookingStatusText,
  getRoomTypeLabel,
} from "../../../constants/constent";
import Ribbon from "antd/es/badge/Ribbon";
import { Collapse } from "antd";
import BookingPopOver from "../BookingPopOver";
import { RedoOutlined } from "@ant-design/icons";

const { Panel } = Collapse;
const { Text } = Typography;

const RoomView = () => {
  const { Content } = Layout;
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD"),
  );

  useEffect(() => {
    fetchRoomData(selectedDate);
  }, []);

  const fetchRoomData = async (date) => {
    setLoading(true);
    const successFn = function (data) {
      setLoading(false);
      setData(data.categories || []);
      setSummary(data.summary || null);
    };

    const errorFn = function () {
      setLoading(false);
      setData([]);
      setSummary(null);
    };
    postAPI(ROOM_VIEW, { date: date }, successFn, errorFn);
  };

  const [selectedBooking, setSelectedBooking] = useState(null);
  // Booking Detail Modal
  const [isBookingPopOverShow, setIsBookingPopOverShow] = useState(false);

  const openModel = (info) => {
    getBookingDetail(info.booking_id);
  };
  const handleClose = () => {
    setIsBookingPopOverShow(false);
    setSelectedBooking(null);
    fetchRoomData(selectedDate);
  };

  const getBookingDetail = (id) => {
    const successFn = function (data) {
      if (data.status === true) {
        setSelectedBooking(data.data);
        setIsBookingPopOverShow(true);
      } else {
        setIsBookingPopOverShow(false);
      }
    };
    const errorFn = function () {
      setIsBookingPopOverShow(false);
    };
    postAPI(BOOKING_BY_ID, { id: id }, successFn, errorFn);
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          loading={loading}
          title="Rooms View"
          extra={
            <>
              {/* Calculate totals for all categories */}
              {(() => {
                const totalGuests = summary?.guests;
                const occupied = summary?.occupied;
                const available = summary?.available;

                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Space size="large">
                      <b>
                        Guests:{" "}
                        <Badge color="green" count={totalGuests} showZero />
                      </b>
                      <b>
                        Occupied:{" "}
                        <Badge color="green" count={occupied} showZero />
                      </b>
                      <b>
                        Available:{" "}
                        <Badge color="green" count={available} showZero />
                      </b>
                      <div>
                        <DatePicker
                          value={dayjs(selectedDate)}
                          format="YYYY-MM-DD"
                          onChange={(date, dateString) => {
                            setSelectedDate(dateString);
                            fetchRoomData(dateString);
                          }}
                        />
                      </div>
                      <Tooltip title="Refresh">
                        <Button
                          icon={<RedoOutlined />}
                          loading={loading}
                          disabled={loading}
                          onClick={() => fetchRoomData(selectedDate)}
                        />
                      </Tooltip>
                    </Space>
                  </div>
                );
              })()}
            </>
          }
        >
          <Collapse
            activeKey={data.map((_, index) => String(index))}
            accordion={false}
          >
            {data.map((category, index) => {
              const totalGuests = category.summary.guests;
              const occupied = category.summary.occupied;
              const available = category.summary.available;

              return (
                <Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{category.category_name}</span>
                      <Space size="large">
                        <b>
                          Guests:{" "}
                          <Badge color="green" count={totalGuests} showZero />
                        </b>
                        <b>
                          Occupied:{" "}
                          <Badge color="green" count={occupied} showZero />
                        </b>
                        <b>
                          Available:{" "}
                          <Badge color="green" count={available} showZero />
                        </b>
                      </Space>
                    </div>
                  }
                  key={String(index)}
                >
                  <Row gutter={[16, 16]}>
                    {category.rooms.map((room) => (
                      <Col key={room.room_id} span={5}>
                        <CardComponent room={room} openModel={openModel} />
                      </Col>
                    ))}
                  </Row>
                </Panel>
              );
            })}
          </Collapse>
        </Card>
        {/* Detail Modal */}
        {isBookingPopOverShow && selectedBooking && (
          <BookingPopOver
            visible={isBookingPopOverShow}
            onClose={handleClose}
            booking={selectedBooking} // Pass the booking data here
          />
        )}
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

const CardComponent = ({ room, openModel }) => {
  return (
    <Card
      styles={{ body: { padding: 10 } }}
      cover={
        <div
          style={{
            position: "relative",
            height: 150,
            backgroundImage: `url(${IMAGE_BASE_URL}${room.room_image[0]?.image?.filePath})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            filter: room.is_blocked ? "grayscale(80%)" : "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: room.is_blocked
                ? "rgba(255,0,0,0.4)"
                : "rgba(0,0,0,0.4)",
              color: "#fff",
              padding: "8px 12px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {room.plan_code} ({room.room_no})
            </div>
            <div style={{ fontSize: 12 }}>
              {getRoomTypeLabel(room.room_type)}
            </div>
          </div>
        </div>
      }
    >
      <div>
        {room.is_blocked ? (
          <Text type="danger">Room is Blocked</Text>
        ) : room.bookings.length > 0 ? (
          room.bookings.map((booking) => (
            <Ribbon
              key={booking.booking_id}
              color={bookingStatusColors[booking.status] || "default"}
              text={bookingStatusText[booking.status] || "Unknown"}
            >
              <div
                onClick={() => openModel(booking)}
                style={{
                  marginBottom: 10,
                  padding: 8,
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                }}
              >
                <Text strong>{booking.guest_name}</Text>
                <br />
                <Text type="secondary">
                  {booking.checkin_date} → {booking.checkout_date}
                </Text>
              </div>
            </Ribbon>
          ))
        ) : (
          <Text type="secondary">No bookings</Text>
        )}
      </div>
    </Card>
  );
};

export default RoomView;
